import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDevMode, setIsDevMode] = useState(false);

  // Verifica se a flag de desenvolvimento está ativada
  const isDevelopmentBypass = process.env.REACT_APP_AUTH_DEV_MODE === 'true';

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    // Se a flag de desenvolvimento estiver ativa, permite acesso sem autenticação
    if (isDevelopmentBypass) {
      console.log('🔓 Modo de desenvolvimento ativo - autenticação ignorada');
      setIsAuthenticated(true);
      setIsDevMode(true);
      setUser({
        email: 'dev@localhost',
        name: 'Desenvolvedor',
      });
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_AUTH_BACKEND_URL}/api/auth/status`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();

        if (data.authenticated) {
          setIsAuthenticated(true);
          setIsDevMode(data.dev_mode || false);
          setUser(data.user);
        } else {
          setIsAuthenticated(false);
          setIsDevMode(false);
          setUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Erro ao verificar status de autenticação:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    // Se estiver em modo de desenvolvimento, não precisa fazer logout no backend
    if (isDevelopmentBypass || isDevMode) {
      setUser(null);
      setIsAuthenticated(false);
      return;
    }

    try {
      await fetch(`${process.env.REACT_APP_AUTH_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isDevMode,
    login,
    logout,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
